import React, { useRef, useEffect, useState } from 'react';
import { Box } from '@mantine/core';

interface AnimatedElementProps {
  children: React.ReactNode;
  animation?: 'fadeInUp' | 'fadeInLeft' | 'fadeInRight' | 'scaleIn' | 'slideInRotate';
  delay?: number;
  duration?: number;
  threshold?: number;
  once?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

const animations = {
  fadeInUp: {
    initial: { opacity: 0, transform: 'translateY(60px)' },
    animate: { opacity: 1, transform: 'translateY(0px)' }
  },
  fadeInLeft: {
    initial: { opacity: 0, transform: 'translateX(-60px)' },
    animate: { opacity: 1, transform: 'translateX(0px)' }
  },
  fadeInRight: {
    initial: { opacity: 0, transform: 'translateX(60px)' },
    animate: { opacity: 1, transform: 'translateX(0px)' }
  },
  scaleIn: {
    initial: { opacity: 0, transform: 'scale(0.8)' },
    animate: { opacity: 1, transform: 'scale(1)' }
  },
  slideInRotate: {
    initial: { opacity: 0, transform: 'translateY(40px) rotate(-5deg)' },
    animate: { opacity: 1, transform: 'translateY(0px) rotate(0deg)' }
  }
};

export const AnimatedElement: React.FC<AnimatedElementProps> = ({
  children,
  animation = 'fadeInUp',
  delay = 0,
  duration = 0.8,
  threshold = 0.1,
  once = true,
  className = '',
  style = {}
}) => {
  const elementRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (once) {
            setHasAnimated(true);
          }
        } else if (!once && !hasAnimated) {
          setIsVisible(false);
        }
      },
      { threshold }
    );

    const element = elementRef.current;
    if (element) {
      observer.observe(element);
    }

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, [threshold, once, hasAnimated]);

  const animationConfig = animations[animation];
  const currentStyle = isVisible ? animationConfig.animate : animationConfig.initial;

  return (
    <Box
      ref={elementRef}
      className={className}
      style={{
        transition: `all ${duration}s cubic-bezier(0.4, 0, 0.2, 1) ${delay}s`,
        ...currentStyle,
        ...style
      }}
    >
      {children}
    </Box>
  );
};

// Staggered animation container
interface StaggeredAnimationProps {
  children: React.ReactNode[];
  staggerDelay?: number;
  animation?: 'fadeInUp' | 'fadeInLeft' | 'fadeInRight' | 'scaleIn' | 'slideInRotate';
  duration?: number;
  threshold?: number;
  className?: string;
}

export const StaggeredAnimation: React.FC<StaggeredAnimationProps> = ({
  children,
  staggerDelay = 0.1,
  animation = 'fadeInUp',
  duration = 0.8,
  threshold = 0.1,
  className = ''
}) => {
  return (
    <Box className={className}>
      {children.map((child, index) => (
        <AnimatedElement
          key={index}
          animation={animation}
          delay={index * staggerDelay}
          duration={duration}
          threshold={threshold}
        >
          {child}
        </AnimatedElement>
      ))}
    </Box>
  );
};

// Parallax container
interface ParallaxElementProps {
  children: React.ReactNode;
  speed?: number;
  direction?: 'up' | 'down' | 'left' | 'right';
  className?: string;
  style?: React.CSSProperties;
}

export const ParallaxElement: React.FC<ParallaxElementProps> = ({
  children,
  speed = 0.5,
  direction = 'up',
  className = '',
  style = {}
}) => {
  const elementRef = useRef<HTMLDivElement>(null);
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const element = elementRef.current;
      if (!element) return;

      const rect = element.getBoundingClientRect();
      const scrolled = window.pageYOffset;
      const rate = scrolled * -speed;

      let transformValue = '';
      switch (direction) {
        case 'up':
          transformValue = `translateY(${rate}px)`;
          break;
        case 'down':
          transformValue = `translateY(${-rate}px)`;
          break;
        case 'left':
          transformValue = `translateX(${rate}px)`;
          break;
        case 'right':
          transformValue = `translateX(${-rate}px)`;
          break;
      }

      element.style.transform = transformValue;
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [speed, direction]);

  return (
    <Box
      ref={elementRef}
      className={className}
      style={{
        willChange: 'transform',
        ...style
      }}
    >
      {children}
    </Box>
  );
};

// Text reveal animation
interface TextRevealProps {
  text: string;
  delay?: number;
  duration?: number;
  className?: string;
  style?: React.CSSProperties;
}

export const TextReveal: React.FC<TextRevealProps> = ({
  text,
  delay = 0,
  duration = 0.05,
  className = '',
  style = {}
}) => {
  const [visibleChars, setVisibleChars] = useState(0);
  const elementRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          let currentChar = 0;
          const interval = setInterval(() => {
            if (currentChar <= text.length) {
              setVisibleChars(currentChar);
              currentChar++;
            } else {
              clearInterval(interval);
            }
          }, duration * 1000);
        }
      },
      { threshold: 0.1 }
    );

    const element = elementRef.current;
    if (element) {
      observer.observe(element);
    }

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, [text, duration]);

  return (
    <Box
      ref={elementRef}
      className={className}
      style={{
        transitionDelay: `${delay}s`,
        ...style
      }}
    >
      {text.split('').map((char, index) => (
        <span
          key={index}
          style={{
            opacity: index < visibleChars ? 1 : 0,
            transition: 'opacity 0.1s ease-in-out'
          }}
        >
          {char}
        </span>
      ))}
    </Box>
  );
};

export default AnimatedElement; 